import React, { useEffect, useMemo, useState } from 'react';
import { FaExternalLinkAlt, FaLink, FaMapMarkerAlt } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import bannerModelImage from './banner-model.png';
import fallbackProfileImage from './fallback-product.png';
import {
  getPlayStoreUrl,
  getProductDeepLinkUrl,
  getProfileDeepLinkUrl,
  getProfileWebUrl,
  openProductInAppOrStore,
  openProfileInAppOrStore,
} from './deepLink';
import './UserProfile.css';

const API_BASE_URL = 'https://x.ladyfirst.me/api/auth';
const MEDIA_BASE_URL = 'https://x.ladyfirst.me';

function UserProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copyState, setCopyState] = useState('idle');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState('');
  const [productFilter, setProductFilter] = useState('active');

  const shareUrl = useMemo(() => getProfileWebUrl(username), [username]);
  const deepLinkUrl = useMemo(
    () => (profile?.id ? getProfileDeepLinkUrl(profile.id) : getPlayStoreUrl()),
    [profile]
  );

  const profileImageUrl = useMemo(() => {
    if (!profile?.profile_picture) return fallbackProfileImage;
    return profile.profile_picture.startsWith('http')
      ? profile.profile_picture
      : `${MEDIA_BASE_URL}/media/${profile.profile_picture}`;
  }, [profile]);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchProfile() {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE_URL}/profile/${encodeURIComponent(username)}/`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Profile not found');
        const data = await response.json();
        setProfile(data);
        document.title = `${data.username || username} | LadyFirst`;
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError('This LadyFirst profile could not be found.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    fetchProfile();
    return () => {
      controller.abort();
      document.title = 'LadyFirst';
    };
  }, [username]);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchProducts() {
      setProductsLoading(true);
      setProductsError('');
      try {
        const response = await fetch(
          `${API_BASE_URL}/user-products/${encodeURIComponent(username)}/`,
          { signal: controller.signal, headers: { Accept: 'application/json' } }
        );
        if (!response.ok) throw new Error('Products unavailable');
        const data = await response.json();
        setProducts(Array.isArray(data) ? data : []);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setProductsError('Products are temporarily unavailable.');
        }
      } finally {
        if (!controller.signal.aborted) setProductsLoading(false);
      }
    }

    fetchProducts();
    return () => controller.abort();
  }, [username]);

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
    } catch (copyError) {
      setCopyState('error');
    }
    window.setTimeout(() => setCopyState('idle'), 1500);
  };

  const openInApp = (event) => {
    if (!profile?.id) return;
    event.preventDefault();
    openProfileInAppOrStore(profile.id, username);
  };

  const openProductInApp = (event, productId) => {
    event.preventDefault();
    openProductInAppOrStore(productId);
  };

  if (loading) {
    return <div className="public-profile-status">Loading profile…</div>;
  }

  if (error) {
    return (
      <div className="public-profile-status public-profile-status--error">
        <h1>Profile unavailable</h1>
        <p>{error}</p>
        <Link to="/">Return to LadyFirst</Link>
      </div>
    );
  }

  const location = [profile.city, profile.province, profile.country].filter(Boolean).join(', ');
  const bio = profile.bio || profile.about || profile.description || 'A member of the LadyFirst community.';
  const activeProducts = products.filter(
    (product) => product.status === 'active' || product.status === 'bidding'
  );
  const soldProducts = products.filter((product) => product.status === 'sold');
  const visibleProducts = productFilter === 'active' ? activeProducts : soldProducts;

  return (
    <main className="public-profile-page">
      <nav className="public-profile-nav" aria-label="Profile navigation">
        <Link className="public-profile-brand" to="/">ladyfirst</Link>
        <a href={deepLinkUrl} onClick={openInApp}>Open in app <FaExternalLinkAlt aria-hidden="true" /></a>
      </nav>

      <section className="public-profile-card" aria-labelledby="profile-name">
        <img
          className="public-profile-avatar"
          src={profileImageUrl}
          alt={`${profile.username || username}'s LadyFirst profile`}
          onError={(event) => { event.currentTarget.src = fallbackProfileImage; }}
        />
        <div className="public-profile-copy">
          <p className="public-profile-label">LADYFIRST PROFILE</p>
          <h1 id="profile-name">{profile.username || username}</h1>
          <p className="public-profile-bio">{bio}</p>
          <div className="public-profile-actions">
            {location && (
              <span><FaMapMarkerAlt aria-hidden="true" /> {location}</span>
            )}
            <button type="button" onClick={copyShareLink}>
              <FaLink aria-hidden="true" />
              {copyState === 'copied' ? 'Link copied' : copyState === 'error' ? 'Copy failed' : 'Share profile'}
            </button>
          </div>
        </div>
      </section>

      <section className="public-profile-products" aria-labelledby="profile-products-title">
        <div className="public-profile-products-heading">
          <div>
            <p className="public-profile-label">SELLER WARDROBE</p>
            <h2 id="profile-products-title">Products</h2>
          </div>
          <div className="public-profile-product-tabs" role="tablist" aria-label="Filter products">
            <button
              type="button"
              role="tab"
              aria-selected={productFilter === 'active'}
              className={productFilter === 'active' ? 'active' : ''}
              onClick={() => setProductFilter('active')}
            >
              Active <span>{activeProducts.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={productFilter === 'sold'}
              className={productFilter === 'sold' ? 'active' : ''}
              onClick={() => setProductFilter('sold')}
            >
              Sold <span>{soldProducts.length}</span>
            </button>
          </div>
        </div>

        {productsLoading ? (
          <div className="public-profile-product-message">Loading products…</div>
        ) : productsError ? (
          <div className="public-profile-product-message public-profile-product-message--error">{productsError}</div>
        ) : visibleProducts.length === 0 ? (
          <div className="public-profile-product-message">
            No {productFilter} products to show.
          </div>
        ) : (
          <div className="public-profile-product-grid" role="tabpanel">
            {visibleProducts.map((product) => {
              const productImage = product.images?.[0]?.image || fallbackProfileImage;
              const price = product.current_bid_price || product.second_hand_price;
              return (
                <a
                  className="public-profile-product-card"
                  href={getProductDeepLinkUrl(product.id)}
                  key={product.id}
                  onClick={(event) => openProductInApp(event, product.id)}
                >
                  <div className="public-profile-product-image-wrap">
                    <img
                      src={productImage}
                      alt={product.title}
                      loading="lazy"
                      onError={(event) => { event.currentTarget.src = fallbackProfileImage; }}
                    />
                    <span className={`public-profile-product-badge public-profile-product-badge--${product.status}`}>
                      {product.status === 'bidding' ? 'Bidding' : productFilter === 'sold' ? 'Sold' : 'Available'}
                    </span>
                  </div>
                  <div className="public-profile-product-info">
                    <h3>{product.title}</h3>
                    {price != null && <strong>฿{price}</strong>}
                    <span>View in LadyFirst app</span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </section>

      <section className="public-profile-app-card">
        <img src={bannerModelImage} alt="LadyFirst mobile fashion marketplace" />
        <div>
          <p>Continue in the LadyFirst app</p>
          <h2>Continue the experience on mobile.</h2>
          <span>Buying, selling, bidding, payment, and messaging are available in the LadyFirst app.</span>
          <a href={deepLinkUrl} onClick={openInApp}>Open LadyFirst</a>
        </div>
      </section>
    </main>
  );
}

export default UserProfile;
